import React, { useState } from 'react';
import { CreditCard, Check, Calendar, AlertCircle, MessageCircle, X, Clock, Lock, TrendingUp, ShieldCheck, CreditCardIcon, Bitcoin, HelpCircle } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { es } from 'date-fns/locale';

const SubscriptionConversion = ({ subscription }) => {
  const [selectedPlan, setSelectedPlan] = useState(subscription?.status === 'free' ? 'inicio' : 'plus');
  const [billingCycle, setBillingCycle] = useState('annual');
  const [showTestimonial, setShowTestimonial] = useState(true);
  
  // Calcular días restantes de prueba
  const daysLeft = subscription?.current_period_end 
    ? Math.ceil((new Date(subscription.current_period_end) - new Date()) / (1000 * 60 * 60 * 24))
    : 0;
  
  const isTrialEnding = daysLeft <= 3 && daysLeft > 0;
  const isFree = subscription?.status === 'free';
  const isCanceled = subscription?.status === 'canceled';
  
  // Formatear la fecha de fin del período para visualización
  const formattedEndDate = subscription?.current_period_end 
    ? format(new Date(subscription.current_period_end), "d 'de' MMMM 'de' yyyy", { locale: es })
    : '';
  
  const plans = [
    {
      id: 'hobby',
      name: 'Hobby',
      price: 0,
      originalPrice: 0,
      features: [
        'Catálogo virtual',
        'Juegos',
        'Plan gratuito',
        'Datos reiniciados cada 7 días'
      ],
      limitations: [
        'Sin exportación de datos',
        'Máximo 200 productos',
        'Máximo 5 usuarios',
        'Soporte limitado'
      ],
      recommended: false,
      isFree: true
    },
    {
      id: 'inicio',
      name: 'Inicio',
      price: billingCycle === 'annual' ? 25 : 30,
      originalPrice: billingCycle === 'annual' ? 25 : 30,
      features: [
        'Todo lo de Hobby más:',
        'Operaciones básicas',
        'Exportar datos',
        'Soporte por WhatsApp',
        'Sin reinicio de datos'
      ],
      recommended: isFree,
      highlightFeatures: [
        'Conserva tus datos para siempre',
        'Exporta tus datos cuando quieras',
        'Soporte dedicado por WhatsApp'
      ]
    },
    {
      id: 'plus',
      name: 'Plus',
      price: billingCycle === 'annual' ? 69 : 83,
      originalPrice: billingCycle === 'annual' ? 69 : 83,
      features: [
        'Todo lo de Inicio más:',
        'Operaciones ilimitadas',
        'Plantillas personalizadas',
        'Reportes avanzados',
        'Roles y permisos'
      ],
      recommended: !isFree && !isCanceled,
      discount: billingCycle === 'annual' ? 17 : 0,
      remainingOffers: 13
    },
    {
      id: 'pro',
      name: 'Pro',
      price: billingCycle === 'annual' ? 149 : 179,
      originalPrice: billingCycle === 'annual' ? 149 : 179,
      features: [
        'Todo lo de Plus más:',
        'Impresoras en comodato',
        'Acceso a nuevas funciones',
        'Más de 15 usuarios',
        'Soporte prioritario 24/7'
      ],
      recommended: false
    },
    {
      id: 'tibo',
      name: 'Tibó',
      price: billingCycle === 'annual' ? 499 : 599,
      originalPrice: billingCycle === 'annual' ? 499 : 599,
      features: [
        'Plan personalizado',
        'Onboarding personalizado',
        'Usuarios ilimitados',
        'Integraciones a medida',
        'Manager de cuenta dedicado'
      ],
      recommended: false,
      isEnterprise: true
    }
  ];

  const formatPrice = (price) => {
    if (price === 0) return 'Gratis';
    
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  const calculateAnnualSavings = (plan) => {
    if (plan.id === 'hobby' || plan.price === 0) return 0;
    
    const monthlyTotal = plans.find(p => p.id === plan.id).price * 1.2 * 12;
    const annualTotal = plan.price * 12;
    
    return monthlyTotal - annualTotal;
  };

  const calculateAnnualTotal = (plan) => {
    if (plan.id === 'hobby' || plan.price === 0) return 0;
    return plan.price * 12;
  };

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
  };

  const handleContinue = () => {
    // Implementar cuando tengamos la integración con Mercado Pago
    console.log(`Actualizar a plan ${selectedPlan} con ciclo ${billingCycle}`);
    alert('En una implementación real, aquí redirigirías a Mercado Pago para completar el pago.');
  };

  const handleWhatsAppContact = () => {
    // Número y mensaje para WhatsApp (cambiar por el número real)
    const phoneNumber = '5491112345678';
    let message = `Hola, tengo una consulta sobre los planes de Tibó.`;
    
    if (isCanceled) {
      message = `Hola, tengo un problema con mi suscripción cancelada. Me gustaría reactivarla.`;
    } else {
      message += ` Estoy interesado en el plan ${plans.find(p => p.id === selectedPlan)?.name}.`;
    }
    
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleSupportContact = () => {
    // Número y mensaje para WhatsApp de soporte (cambiar por el número real)
    const phoneNumber = '5491112345678';
    const message = `Hola, necesito ayuda con mi suscripción cancelada. Mi plan estará activo hasta el ${formattedEndDate}. Me gustaría reactivarla.`;
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  // Días restantes para reinicio de datos (solo para plan gratuito)
  const daysToDataReset = isFree ? (7 - new Date().getDay()) % 7 || 7 : 0;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Banner de alerta - diferentes estados */}
      {isFree ? (
        <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-amber-700 dark:text-amber-300">
                Plan gratuito con tiempo limitado
              </h3>
              <p className="text-sm mt-1 text-gray-600 dark:text-gray-300">
                Tus datos se reiniciarán en <strong>{daysToDataReset} {daysToDataReset === 1 ? 'día' : 'días'}</strong>. Actualiza a un plan pago para conservar tu información y acceder a más funcionalidades.
              </p>
            </div>
          </div>
        </div>
      ) : isCanceled ? (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-gray-700 dark:text-gray-300">
                Suscripción cancelada
              </h3>
              <p className="text-sm mt-1 text-gray-600 dark:text-gray-400">
                Tu suscripción ha sido cancelada. Podrás seguir utilizando el servicio hasta el <strong>{formattedEndDate}</strong>. 
                Después de esa fecha, tu cuenta pasará al plan gratuito y perderás acceso a funciones premium.
              </p>
              
              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  onClick={handleSupportContact}
                  className="inline-flex items-center gap-2 text-white bg-primary-500 hover:bg-primary-600 px-3 py-1.5 rounded-lg text-sm"
                >
                  <HelpCircle className="w-4 h-4" />
                  ¿Problemas con tu suscripción?
                </button>
                
                <button
                  onClick={() => setSelectedPlan(subscription?.plan_name?.toLowerCase() || 'plus')}
                  className="inline-flex items-center gap-2 text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 px-3 py-1.5 rounded-lg text-sm"
                >
                  <Check className="w-4 h-4" />
                  Reactivar mi suscripción
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : subscription?.status === 'trial' && (
        <div className={`mb-6 p-4 rounded-lg ${
          isTrialEnding
            ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
            : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
        }`}>
          <div className="flex items-start gap-3">
            {isTrialEnding ? (
              <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
            ) : (
              <Calendar className="w-5 h-5 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            )}
            <div>
              <h3 className={`font-medium ${
                isTrialEnding 
                  ? 'text-red-700 dark:text-red-300'
                  : 'text-blue-700 dark:text-blue-300'
              }`}>
                {isTrialEnding
                  ? '¡Tu período de prueba está por finalizar!'
                  : 'Período de prueba en curso'}
              </h3>
              <p className="text-sm mt-1 text-gray-600 dark:text-gray-300">
                {daysLeft > 0
                  ? `Te quedan ${daysLeft} ${daysLeft === 1 ? 'día' : 'días'} de prueba gratuita. ${
                      isTrialEnding
                        ? 'Actualiza ahora para seguir disfrutando de todas las funcionalidades.'
                        : 'Explora todas las funcionalidades premium durante este período.'
                    }`
                  : 'Tu período de prueba ha finalizado. Actualiza ahora para seguir disfrutando de todas las funcionalidades.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Limitaciones del plan gratuito */}
      {isFree && (
        <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-5">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <Lock className="w-5 h-5 mr-2 text-amber-500" />
            Limitaciones del plan gratuito
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-2">
              <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-gray-700 dark:text-gray-300">Tus datos se reiniciarán automáticamente cada 7 días</p>
            </div>
            <div className="flex items-start gap-2">
              <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-gray-700 dark:text-gray-300">No podrás exportar tus datos</p>
            </div>
            <div className="flex items-start gap-2">
              <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-gray-700 dark:text-gray-300">Máximo 5 usuarios simultáneos</p>
            </div>
            <div className="flex items-start gap-2">
              <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-gray-700 dark:text-gray-300">Soporte básico por foro solamente</p>
            </div>
          </div>
        </div>
      )}

      {/* Beneficios destacados del primer plan pago para usuarios gratuitos o que cancelaron */}
      {(isFree || isCanceled) && (
        <div className="mb-6 bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/30 dark:to-blue-900/30 border border-primary-100 dark:border-primary-800 rounded-lg p-5">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <TrendingUp className="w-5 h-5 mr-2 text-primary-500" />
            {isCanceled ? '¿Por qué reactivar tu suscripción?' : '¿Por qué actualizar a Inicio?'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-800 flex items-center justify-center mb-3">
                <Check className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-1">Conserva tus datos</h4>
              <p className="text-sm text-center text-gray-600 dark:text-gray-400">
                {isCanceled 
                  ? 'Evita perder tus datos cuando termine tu período actual'
                  : 'Tus datos nunca se reiniciarán ni perderán con el plan Inicio'
                }
              </p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-800 flex items-center justify-center mb-3">
                <ShieldCheck className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-1">Exporta tus datos</h4>
              <p className="text-sm text-center text-gray-600 dark:text-gray-400">Exporta tus informes y datos cuando quieras</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-800 flex items-center justify-center mb-3">
                <MessageCircle className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-1">Soporte directo</h4>
              <p className="text-sm text-center text-gray-600 dark:text-gray-400">Accede a soporte por WhatsApp con respuesta garantizada</p>
            </div>
          </div>
          <div className="mt-5 text-center">
            <button 
              onClick={() => {
                setSelectedPlan(isCanceled ? (subscription?.plan_name?.toLowerCase() || 'plus') : 'inicio');
                document.getElementById('subscription-plans').scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-5 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm"
            >
              {isCanceled 
                ? 'Reactivar mi suscripción' 
                : `Actualizar a Inicio por solo ${billingCycle === 'annual' ? 25 : 30}/mes`
              }
            </button>
          </div>
        </div>
      )}

      {/* Testimonial */}
      {showTestimonial && (
        <div className="mb-6 bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800 rounded-lg p-5 relative">
          <button
            onClick={() => setShowTestimonial(false)}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
            aria-label="Cerrar testimonial"
          >
            ×
          </button>
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-primary-100 dark:bg-primary-800 rounded-full flex items-center justify-center mb-3">
              <span className="text-primary-700 dark:text-primary-300 text-xl font-bold">JM</span>
            </div>
            <p className="text-gray-700 dark:text-gray-300 italic mb-3">
              "Desde que actualizamos a Plus, nuestras ventas aumentaron un 30%. El control de lotes nos ayudó a reducir el desperdicio y mejorar nuestros márgenes."
            </p>
            <p className="font-medium text-gray-900 dark:text-white">José Martínez</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Frutas del Sur, Bahía Blanca</p>
          </div>
        </div>
      )}

      {/* Selector de ciclo de facturación */}
      <div className="mb-6 flex justify-center">
        <div className="inline-flex p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
          <button
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              billingCycle === 'monthly'
                ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
            onClick={() => setBillingCycle('monthly')}
          >
            Mensual (+20%)
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium rounded-md flex items-center gap-2 ${
              billingCycle === 'annual'
                ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
            onClick={() => setBillingCycle('annual')}
          >
            Anual
            <span className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300 text-xs px-2 py-0.5 rounded-full">
              MEJOR VALOR
            </span>
          </button>
        </div>
      </div>

      {/* Tarjetas de planes */}
      <div id="subscription-plans" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`
              bg-white dark:bg-gray-800 rounded-lg overflow-hidden transition-all
              ${plan.recommended 
                ? 'ring-2 ring-primary-500 dark:ring-primary-400 shadow-md scale-105 relative z-10' 
                : 'border border-gray-200 dark:border-gray-700 shadow-sm'
              }
              ${selectedPlan === plan.id 
                ? 'ring-2 ring-primary-500 dark:ring-primary-400' 
                : ''
              }
              ${plan.isEnterprise ? 'bg-gray-900 text-white' : ''}
            `}
          >
            {plan.recommended && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-primary-500 text-white px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                  {isFree && plan.id === 'inicio' 
                    ? 'Mejor para empezar' 
                    : isCanceled && plan.id === (subscription?.plan_name?.toLowerCase() || 'plus')
                      ? 'Tu plan anterior'
                      : 'Recomendado'
                  }
                </span>
              </div>
            )}
            <div className="p-5">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className={`text-lg font-bold ${plan.isEnterprise ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                    {plan.name}
                  </h3>
                  <div className="mt-1">
                    {plan.originalPrice !== plan.price && !plan.isFree && (
                      <span className="text-sm text-gray-500 dark:text-gray-400 line-through mr-2">
                        {formatPrice(plan.originalPrice)}
                      </span>
                    )}
                    <span className={`text-2xl font-bold ${plan.isEnterprise ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                      {formatPrice(plan.price)}
                    </span>
                    {!plan.isFree && (
                      <span className={`${plan.isEnterprise ? 'text-gray-300' : 'text-gray-500 dark:text-gray-400'} text-sm`}>
                        /mes
                      </span>
                    )}
                  </div>
                  
                  {/* Mostrar precio anual total de forma más explícita */}
                  {billingCycle === 'annual' && !plan.isFree && (
                    <div className="mt-1 flex items-center">
                      <span className={`text-sm ${plan.isEnterprise ? 'text-gray-300' : 'text-gray-700 dark:text-gray-300'}`}>
                        Total anual: <strong>{formatPrice(calculateAnnualTotal(plan))}</strong>
                      </span>
                    </div>
                  )}
                  
                  {billingCycle === 'annual' && !plan.isFree && calculateAnnualSavings(plan) > 0 && (
                    <div className="mt-1">
                      <span className="text-xs text-green-600 dark:text-green-400">
                        Ahorrás ${calculateAnnualSavings(plan)} al año
                      </span>
                    </div>
                  )}
                  {plan.discount && (
                    <div className="mt-1">
                      <span className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300 text-xs px-2 py-0.5 rounded-full">
                        {plan.discount}% OFF
                      </span>
                      {plan.remainingOffers && (
                        <span className="text-xs text-green-600 dark:text-green-400 ml-1">
                          {plan.remainingOffers} suscripciones restantes
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div 
                  className={`w-5 h-5 rounded-full ${
                    selectedPlan === plan.id
                      ? 'bg-primary-500 dark:bg-primary-400'
                      : `border-2 ${plan.isEnterprise ? 'border-gray-600' : 'border-gray-300 dark:border-gray-600'}`
                  } flex items-center justify-center cursor-pointer`}
                  onClick={() => handlePlanSelect(plan.id)}
                >
                  {selectedPlan === plan.id && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
              </div>
              
              <ul className="space-y-2 mb-5">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className={`w-5 h-5 ${plan.isEnterprise ? 'text-white' : 'text-primary-500 dark:text-primary-400'} flex-shrink-0 mt-0.5`} />
                    <span className={`${plan.isEnterprise ? 'text-gray-300' : 'text-gray-700 dark:text-gray-300'} text-sm`}>
                      {feature}
                    </span>
                  </li>
                ))}
                
                {plan.isFree && plan.limitations && (
                  <>
                    <li className="pt-2 mt-2 border-t border-gray-200 dark:border-gray-700">
                      <span className="text-sm font-medium text-red-500 dark:text-red-400">Limitaciones:</span>
                    </li>
                    {plan.limitations.map((limitation, index) => (
                      <li key={`limitation-${index}`} className="flex items-start gap-2">
                        <X className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 dark:text-gray-300 text-sm">
                          {limitation}
                        </span>
                      </li>
                    ))}
                  </>
                )}
              </ul>
              
              <button
                className={`w-full py-2 rounded-lg font-medium ${
                  selectedPlan === plan.id
                    ? plan.isEnterprise
                      ? 'bg-white text-gray-900 hover:bg-gray-100'
                      : 'bg-primary-500 hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-700 text-white'
                    : plan.isEnterprise
                      ? 'bg-gray-700 hover:bg-gray-600 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white'
                }`}
                onClick={() => handlePlanSelect(plan.id)}
              >
                {selectedPlan === plan.id ? 'Seleccionado' : 'Seleccionar'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Botón de WhatsApp */}
      <div className="mb-6 text-center">
        <button 
          onClick={handleWhatsAppContact}
          className="inline-flex items-center gap-2 text-white bg-green-500 hover:bg-green-600 px-4 py-2 rounded-lg font-medium"
        >
          <MessageCircle className="w-5 h-5" />
          Consultar por WhatsApp
        </button>
      </div>

      {/* Información de seguridad, MercadoPago y métodos de pago */}
      <div className="mb-6 text-center">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Métodos de pago aceptados</h4>
        <div className="flex flex-wrap justify-center gap-4 mb-4">
          {/* MercadoPago */}
          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg flex items-center gap-2">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18.8446 12.8318C18.8446 16.5889 15.7505 19.6637 11.9833 19.6637C8.21623 19.6637 5.12207 16.5889 5.12207 12.8321C5.12207 9.07496 8.21623 6 11.9833 6C15.7505 6 18.8446 9.07496 18.8446 12.8318Z" fill="#00B1EA"/>
              <path d="M14.4772 15.3843C14.2543 16.1353 13.4101 16.5582 12.656 16.3358C11.9031 16.1143 11.4805 15.2733 11.7023 14.5201C11.9254 13.77 12.7695 13.3471 13.5226 13.5685C14.2767 13.7909 14.7003 14.6319 14.4772 15.3843Z" fill="white"/>
              <path d="M15.6136 11.0089C15.3905 11.76 14.5463 12.1826 13.7922 11.9612C13.0393 11.7388 12.6166 10.8978 12.8385 10.1456C13.0616 9.39447 13.9057 8.97068 14.6588 9.19318C15.4129 9.41568 15.8367 10.2567 15.6136 11.0089Z" fill="white"/>
              <path d="M11.1281 10.1456C11.351 9.39447 10.9284 8.55341 10.1743 8.33091C9.42121 8.10954 8.57829 8.53221 8.35515 9.28329C8.13202 10.0344 8.55577 10.8754 9.30888 11.0979C10.063 11.3203 10.9059 10.8966 11.1281 10.1456Z" fill="white"/>
              <path d="M10.122 13.5059C9.36888 13.2835 8.52484 13.7061 8.30282 14.4583C8.07969 15.2094 8.50344 16.0504 9.25766 16.2729C10.0107 16.4942 10.8536 16.0716 11.0768 15.3194C11.3 14.5683 10.8762 13.7284 10.122 13.5059Z" fill="white"/>
            </svg>
            <span className="text-sm text-gray-600 dark:text-gray-300">Mercado Pago</span>
          </div>
          
          {/* Tarjetas de crédito */}
          <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg flex items-center gap-2">
            <CreditCardIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <span className="text-sm text-gray-600 dark:text-gray-300">Tarjetas de crédito/débito</span>
          </div>
          
          {/* Criptomonedas */}
          <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg flex items-center gap-2">
            <Bitcoin className="w-5 h-5 text-amber-500 dark:text-amber-400" />
            <span className="text-sm text-gray-600 dark:text-gray-300">Bitcoin, Ether y otras cripto</span>
          </div>
        </div>
        
        <p className="text-xs text-gray-500 dark:text-gray-400 max-w-lg mx-auto">
          Todos los pagos son procesados de forma segura. Puedes cancelar en cualquier momento. 
          No hay compromisos a largo plazo. Aceptamos pagos con tarjetas de crédito, débito, 
          Mercado Pago y criptomonedas (Bitcoin, Ethereum y otras).
        </p>
      </div>

      {/* Botón de acción */}
      <div className="flex justify-center">
        <button
          onClick={handleContinue}
          className="px-6 py-3 bg-primary-500 hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-700 text-white font-medium rounded-lg shadow-sm transition-colors"
        >
          {isCanceled ? 'Reactivar suscripción' : 'Continuar con'} {plans.find(p => p.id === selectedPlan)?.name || 'Plan seleccionado'}
          {billingCycle === 'annual' && !plans.find(p => p.id === selectedPlan)?.isFree && (
            <span className="block text-sm font-normal mt-1">
              Total anual: {formatPrice(calculateAnnualTotal(plans.find(p => p.id === selectedPlan)))}
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

export default SubscriptionConversion; 